import api from '@pray/shared/utils/api';

export type TranslationAttemptStatus =
  | 'reserving'
  | 'queued'
  | 'in_progress'
  | 'ready_for_review'
  | 'promoting'
  | 'completed'
  | 'failed'
  | 'discarded'
  | 'cancelled';

export type TranslationAttemptMode = 'video_lip_sync' | 'audio_only';

export type TranslationAttemptReason =
  | 'translation_inaccurate'
  | 'voice_quality'
  | 'lip_sync'
  | 'timing_or_pacing'
  | 'other_quality_issue';

export type TranslationCandidate = {
  duration: number | null;
  file_size: number | null;
  thumbnail_url: string | null;
  transcript: string | null;
  video_url: string;
};

export type TranslationAttempt = {
  id: string;
  status: TranslationAttemptStatus;
  mode: TranslationAttemptMode;
  reason: TranslationAttemptReason;
  credit: {
    amount: number | null;
    currency_code: string | null;
    status: 'not_required' | 'reserved' | 'completed' | 'refunded';
  };
  candidate: TranslationCandidate | null;
  failure_code: string | null;
  candidate_ready_at: string | null;
  completed_at: string | null;
  started_at: string | null;
};

export type TranslationAttemptOverview = {
  attempt: TranslationAttempt | null;
  quote:
    | {
        amount: number;
        balance: number;
        can_cover: boolean;
        currency_code: string;
        enabled: true;
      }
    | {
        amount: null;
        balance: null;
        can_cover: true;
        currency_code: null;
        enabled: false;
      }
    | null;
  retry: {
    attempts_remaining: number;
    retry_available_at: string | null;
  };
};

type TranslationAttemptScope = {
  artistId: string;
  contentId: string;
  locale: string;
};

type StartTranslationAttemptParams = TranslationAttemptScope & {
  creditConfirmationId?: string;
  idempotencyKey: string;
  matchOriginalDuration: boolean;
  mode: TranslationAttemptMode;
  reason: TranslationAttemptReason;
};

type ReviewTranslationAttemptParams = TranslationAttemptScope & {
  action: 'accept' | 'discard';
  attemptId: string;
};

type TranslationAttemptMutationResponse = {
  id: string;
  status: TranslationAttemptStatus;
};

function buildTranslationAttemptsUrl({ artistId, contentId, locale }: TranslationAttemptScope): string {
  if (!artistId) throw Error('Artist ID is required');
  if (!contentId) throw Error('Content ID is required');
  if (!locale) throw Error('Locale is required');

  return `/web/studio/artists/${artistId}/content/${contentId}/locales/${locale}/translation-attempts`;
}

export async function getTranslationAttemptOverview(
  params: TranslationAttemptScope
): Promise<TranslationAttemptOverview> {
  const response = await api.executeGetRequest<{ data: TranslationAttemptOverview }>(
    buildTranslationAttemptsUrl(params)
  );

  return response.data.data;
}

export async function startTranslationAttempt(
  params: StartTranslationAttemptParams
): Promise<TranslationAttemptMutationResponse> {
  const response = await api.executePostRequest<{ data: TranslationAttemptMutationResponse }>(
    buildTranslationAttemptsUrl(params),
    {
      idempotency_key: params.idempotencyKey,
      match_original_duration: params.matchOriginalDuration,
      mode: params.mode,
      reason: params.reason,
      ...(params.creditConfirmationId && { credit_confirmation_id: params.creditConfirmationId }),
    }
  );

  return response.data.data;
}

export async function reviewTranslationAttempt(
  params: ReviewTranslationAttemptParams
): Promise<TranslationAttemptMutationResponse> {
  const url = `${buildTranslationAttemptsUrl(params)}/${params.attemptId}/review`;
  const response = await api.executePostRequest<{ data: TranslationAttemptMutationResponse }>(url, {
    action: params.action,
  });

  return response.data.data;
}
